<script setup lang="ts">
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { RouterLinkBack, VAvatar, VBadge, VButtonInvisible, VMailTo, VSkeletonBar } from '@/modules/shared/components'
import { computed } from 'vue'
import { useIndividualStore } from '../stores/better-individual-store'
import { useRoute } from 'vue-router'
import { profileStatusInfo } from '../stores/investor-store'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { Status } from '@/modules/screening/types'
import AccreditationBadge from '@/modules/screening/component/AccreditationBadge.vue'
import ScreeningCheckWrapper from '@/modules/screening/component/CheckWrapper.vue'
import { useAccreditationStore } from '@/modules/screening/stores/accreditation-store'
import ScreeningAccreditationWrapper from '@/modules/screening/component/AccreditationWrapper.vue'

const { individual_id } = useRoute().params as { individual_id: string; slug: string }

///////////////////////////////////////////////////////////////////////////////
// Calls
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const individualStore = useIndividualStore()
const accreditationStore = useAccreditationStore()
const individual = computed(() => individualStore.individual)
const skeleton = computed(() => !individual.value)

const accreditationCallback = () => {
  const accreditation = accreditationStore.screeningAccreditation
  individualStore.individual = {
    ...individualStore.individual,
    screening_accreditation_status: accreditation.status,
    screening_accreditation_id: accreditation.id,
  }
}

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const isAdmin = computed(() => authStore.is_site_or_group_admin)
const is_viewing_own_profile = computed(() => authStore.current_user?.investor_id?.toString() === individual_id)

///////////////////////////////////////////////////////////////////////////////
// Statuses
///////////////////////////////////////////////////////////////////////////////

const profileBadge = computed(() => profileStatusInfo[individual.value?.profile_status])
</script>

<template>
  <header class="mb-6 px-4 sm:px-0">
    <div class="-mt-8 mb-6">
      <RouterLinkBack
        v-if="isAdmin"
        :to="{ name: 'investing.individuals' }"
        :fallback="{ name: 'individuals.overview' }"
      >
        <VButtonInvisible>← Back to all Individuals</VButtonInvisible>
      </RouterLinkBack>
    </div>

    <div class="flex items-center gap-5">
      <RouterLink :to="{ name: 'investing.individual.account', params: { individual_id: individual_id } }">
        <VAvatar :image="individual?.profile_picture" :initials="individual?.initials" size="lg" />
      </RouterLink>
      <div class="flex max-w-[70%] flex-col gap-3">
        <VSkeletonBar v-if="skeleton" />

        <h2
          v-if="!skeleton"
          class="whitespace-nowrap text-3xl font-semibold leading-7 text-gray-900 dark:text-gray-300"
        >
          {{ individual?.name }}
        </h2>
        <div v-if="individual?.email" class="-mt-2">
          <VSkeletonBar v-if="skeleton" />
          <VMailTo v-else :email="individual?.email" class="incognito text-sm" />
        </div>
        <div class="flex items-center justify-start space-x-1.5 text-xs">
          <template v-if="skeleton">
            <VSkeletonBar />
            <VSkeletonBar />
            <VSkeletonBar />
          </template>
          <template v-else>
            <ScreeningAccreditationWrapper
              v-if="workspaceStore.current.options.is_accreditation_required"
              :screening_accreditation_id="individual?.screening_accreditation_id"
              :status="individual?.screening_accreditation_status"
              :can_edit="true"
              :callback="accreditationCallback"
            />

            <ScreeningCheckWrapper
              v-if="individual?.screening_check_id"
              :checkId="individual?.screening_check_id"
              :status="individual?.screening_check_status as Status"
            />

            <template v-if="profileBadge">
              <RouterLink :to="{ name: 'investing.individual.account' }">
                <VBadge :color="profileBadge.color">{{ profileBadge.label }}</VBadge>
              </RouterLink>
            </template>
          </template>
        </div>
      </div>
    </div>
    <div class="mb-6 sm:mb-0">
      <div class="flex items-center justify-end space-x-2">
        <slot name="actions"></slot>
      </div>
    </div>
  </header>
</template>
